import { useEffect } from "react";
import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { NanaeHeader, NanaeFooter, QSectionHeader } from "@/components/qv3";
import { useAuth } from "@/contexts/AuthContext";
import { socialMeta } from "@/lib/seo-meta";

interface SettingsSearch {
  checkout?: "success" | "cancel";
}

export const Route = createFileRoute("/settings")({
  validateSearch: (search: Record<string, unknown>): SettingsSearch => ({
    checkout:
      search.checkout === "success" || search.checkout === "cancel"
        ? search.checkout
        : undefined,
  }),
  head: () => ({
    meta: [{ title: "Paramètres — Hōjin" }, { name: "robots", content: "noindex, nofollow" },
      ...socialMeta({
        title: "Paramètres — Hōjin",
        description: "Paramètres — Hōjin",
        url: "https://hojin.ai/settings",
        image: "secretaire",
        card: "summary_large_image",
      }),
    ],
    links: [{ rel: "canonical", href: "https://hojin.ai/settings" }],
  }),
  component: SettingsPage,
});

function SettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { checkout: checkoutStatus } = useSearch({ from: "/settings" });

  // Quand on revient de Stripe, on rafraîchit les caches souscription/profil.
  useEffect(() => {
    if (checkoutStatus === "success") {
      queryClient.invalidateQueries({ queryKey: ["subscription"] });
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      queryClient.invalidateQueries({ queryKey: ["profile-stats"] });
    }
  }, [checkoutStatus, queryClient]);

  // Sans état checkout, on renvoie sur le panneau Paramètres habituel.
  useEffect(() => {
    if (!checkoutStatus) {
      navigate({ to: "/compte/parametres", replace: true });
    }
  }, [checkoutStatus, navigate]);

  return (
    <div className="nanae-page qv3-dark">
      <NanaeHeader
        title="Paramètres"
        user={
          user
            ? {
                email: user.email,
                name:
                  (user.user_metadata as { first_name?: string } | undefined)
                    ?.first_name ?? user.email,
              }
            : undefined
        }
      />

      <main className="nanae-page-main">
        {checkoutStatus === "success" && (
          <section style={{ marginBottom: 48 }}>
            <QSectionHeader number="I" title="ABONNEMENT" />
            <div
              style={{
                marginTop: 24,
                border: "1px solid #FF1744",
                background: "color-mix(in oklab, #FF1744 8%, transparent)",
                padding: "28px 24px",
                display: "flex",
                flexDirection: "column",
                gap: 16,
              }}
            >
              <h2
                className="qv3-mono-hud is-caps"
                style={{ fontSize: 12, letterSpacing: 2, margin: 0, color: "#FF1744" }}
              >
                Abonnement activé
              </h2>
              <div
                style={{
                  fontFamily: "var(--qv3-font-serif)",
                  fontSize: 32,
                  lineHeight: 1.15,
                }}
              >
                30 débats par mois inclus.
              </div>
              <p
                style={{
                  fontFamily: "var(--qv3-font-serif)",
                  fontStyle: "italic",
                  color: "var(--qv3-grey-400)",
                  margin: 0,
                }}
              >
                Merci. Votre Conseil vous attend.
              </p>
              <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
                <Link to="/saisine" className="nanae-cta-large">
                  Saisir le Conseil
                </Link>
                <Link
                  to="/compte/facturation"
                  style={{
                    fontFamily: "var(--qv3-font-mono)",
                    fontSize: 11,
                    letterSpacing: "0.25em",
                    textTransform: "uppercase",
                    color: "var(--qv3-gold, #FF1744)",
                    textDecoration: "none",
                    borderBottom: "0.5px solid #FF1744",
                    paddingBottom: 4,
                    alignSelf: "center",
                  }}
                >
                  Voir la facturation →
                </Link>
              </div>
            </div>
          </section>
        )}
      </main>

      <NanaeFooter variant="minimal" />
    </div>
  );
}
